import {
    Box,
    Checkbox,
    Chip,
    Collapse,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
    DATATYPES_ENUM,
    EXTRACTION_MS_TGDT_UUID,
    PIPELINE_INSTANCE_STATUS,
} from "../../../constants";
import { compareDesc, parseISO } from "date-fns";
import { useMemo, useState } from "react";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LinkOff from "@mui/icons-material/LinkOff";
import { MODEL_PROPTYPES } from "../../../models";
import { PipelineInstanceItem } from "./PipelineInstanceItem";
import { PipelineInstanceStatusPieChart } from "../PipelineInstanceStatusPieChart";
import PropTypes from "prop-types";
import { rcompare } from "semver";

export const TraitGroup = ({
    fieldArrayName,
    name,
    dataType,
    pipelines,
    pipelineInstances,
    validatedPipelineInstance,
    uuid,
    traitGroupDataTypeUuid,
}) => {
    const theme = useTheme();
    const { setValue } = useFormContext();
    const [open, setOpen] = useState(false);

    const noPipelines = !pipelines.length;
    const versionedModes = useMemo(
        () => getVersionedModes(pipelines),
        [pipelines]
    );

    // Sort PI by createdAt DESC and unshift validated PI
    const sortedPipelineInstances = useMemo(() => {
        const tempSortedPI = pipelineInstances.toSorted(
            ({ createdAt: createdAt1 }, { createdAt: createdAt2 }) =>
                compareDesc(parseISO(createdAt1), parseISO(createdAt2))
        );

        if (validatedPipelineInstance) {
            const index = tempSortedPI.findIndex(
                ({ uuid }) => uuid === validatedPipelineInstance.uuid
            );

            // The validated pipeline instance should always be find in the pipeline instance list
            // Do not unshift if already at the top
            if (index > 0) {
                const pipelineInstanceToUnshift = tempSortedPI[index];

                tempSortedPI.splice(index, 1);
                tempSortedPI.unshift(pipelineInstanceToUnshift);
            }
        }

        return tempSortedPI;
    }, [pipelineInstances, validatedPipelineInstance]);

    const pipelineInstancesCountPerStatus = useMemo(() => {
        const dataObject = sortedPipelineInstances.reduce(
            (total, instance) => {
                total[instance.status].value += 1;
                return total;
            },
            {
                [PIPELINE_INSTANCE_STATUS.PLANNED]: {
                    value: 0,
                    color: theme.palette.grey[400],
                },
                [PIPELINE_INSTANCE_STATUS.ONGOING]: {
                    value: 0,
                    color: theme.palette.info.main,
                },
                [PIPELINE_INSTANCE_STATUS.SUSPENDED]: {
                    value: 0,
                    color: theme.palette.warning.main,
                },
                [PIPELINE_INSTANCE_STATUS.FINISHED]: {
                    value: 0,
                    color: theme.palette.success.main,
                },
                [PIPELINE_INSTANCE_STATUS.ERROR]: {
                    value: 0,
                    color: theme.palette.error.main,
                },
                [PIPELINE_INSTANCE_STATUS.ABORTED]: {
                    value: 0,
                    color: theme.palette.error.dark,
                },
            }
        );
        return Object.keys(dataObject).map((key) => ({
            label: key,
            value: dataObject[key].value,
            color: dataObject[key].color,
        }));
    }, [sortedPipelineInstances, theme.palette]);

    const modes = Object.keys(versionedModes);
    const defaultMode = noPipelines
        ? ""
        : // There should always be a default mode this is a failsafe
          (modes.find((key) => versionedModes[key].isDefault) ?? modes[0]);

    const { mode: selectedMode, checked } = useWatch({
        name: fieldArrayName,
        defaultValue: {
            mode: defaultMode,
            checked:
                traitGroupDataTypeUuid === EXTRACTION_MS_TGDT_UUID
                    ? false
                    : !noPipelines,
        },
    });

    const versions = versionedModes[selectedMode]?.versions ?? [];

    return (
        <>
            <ListItem disablePadding>
                <ListItemIcon>
                    <Controller
                        name={`${fieldArrayName}.checked`}
                        defaultValue={
                            traitGroupDataTypeUuid === EXTRACTION_MS_TGDT_UUID
                                ? false
                                : !noPipelines
                        }
                        disabled={noPipelines}
                        render={({ field: { onChange, value, ...field } }) => (
                            <Checkbox
                                {...field}
                                edge="end"
                                checked={value}
                                onChange={(event) =>
                                    onChange(event.target.checked)
                                }
                                onMouseDown={(event) => event.stopPropagation()}
                                // TODO: add a complicated contraint later
                                /*disabled={
                                        uuid ===
                                        IMAGE_SELECTION_RGB_TGDT_UUID
                                        }*/
                            />
                        )}
                    />
                </ListItemIcon>
                <ListItemButton
                    sx={{ borderRadius: 1 }}
                    disabled={noPipelines}
                    onClick={() => setOpen(!open)}
                >
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                    >
                        <Grid size={3}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Chip
                                    label={dataType}
                                    size="small"
                                    sx={{
                                        width: "fit-content",
                                        fontSize: "x-small",
                                        p: 0,
                                    }}
                                />
                                <ListItemText primary={name} />
                            </Box>
                        </Grid>
                        {noPipelines ? (
                            <Grid
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                size={8}
                            >
                                <LinkOff />
                                <Typography>
                                    No pipeline for this trait group
                                </Typography>
                            </Grid>
                        ) : (
                            <>
                                <Grid size={2}>
                                    <PipelineInstanceStatusPieChart
                                        pipelineInstancesCountPerStatus={
                                            pipelineInstancesCountPerStatus
                                        }
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Controller
                                        name={`${fieldArrayName}.mode`}
                                        defaultValue={defaultMode}
                                        disabled={noPipelines}
                                        render={({
                                            field: {
                                                onChange,
                                                disabled,
                                                ...field
                                            },
                                        }) => (
                                            <TextField
                                                {...field}
                                                id={`${fieldArrayName}.mode`}
                                                disabled={disabled || !checked}
                                                label="Mode"
                                                fullWidth
                                                select
                                                onMouseDown={(event) =>
                                                    event.stopPropagation()
                                                }
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                                onChange={(event) => {
                                                    onChange(
                                                        event.target.value
                                                    );
                                                    setValue(
                                                        `${fieldArrayName}.version`,
                                                        versionedModes[
                                                            event.target.value
                                                        ].versions[0]
                                                    );
                                                }}
                                            >
                                                {Object.keys(
                                                    versionedModes
                                                ).map((mode) => (
                                                    <MenuItem
                                                        key={mode}
                                                        value={mode}
                                                    >
                                                        {mode}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <Controller
                                        name={`${fieldArrayName}.version`}
                                        defaultValue={versions[0] ?? ""}
                                        disabled={noPipelines}
                                        render={({
                                            field: { disabled, ...field },
                                        }) => (
                                            <TextField
                                                {...field}
                                                id={`${fieldArrayName}.version`}
                                                label="Version"
                                                fullWidth
                                                select
                                                disabled={
                                                    disabled ||
                                                    !checked ||
                                                    !versions.length
                                                }
                                                onMouseDown={(event) =>
                                                    event.stopPropagation()
                                                }
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                            >
                                                {versions.map((version) => (
                                                    <MenuItem
                                                        key={version}
                                                        value={version}
                                                    >
                                                        {version}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                {open ? <ExpandLess /> : <ExpandMore />}
                            </>
                        )}
                    </Grid>
                </ListItemButton>
            </ListItem>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                    component="div"
                    sx={{
                        mx: 7,
                        maxHeight: 220,
                        overflowY:
                            sortedPipelineInstances.length > 4
                                ? "scroll"
                                : "auto",
                    }}
                    disablePadding
                    dense
                >
                    {sortedPipelineInstances.map((pipelineInstance) => (
                        <PipelineInstanceItem
                            key={pipelineInstance.uuid}
                            isValidated={
                                validatedPipelineInstance?.uuid ===
                                pipelineInstance.uuid
                            }
                            pipelineInstance={pipelineInstance}
                            // Once associated to a pipeline instance a pipeline can't be deleted, there should always be one
                            pipeline={pipelines.find(
                                ({ uuid }) =>
                                    uuid ===
                                    pipelineInstance.PipelineTraitGroup
                                        .pipelineUuid
                            )}
                        />
                    ))}
                </List>
            </Collapse>
        </>
    );
};

// Returns an object of the form { [mode]: { versions[], defaultMode?: string } }
const getVersionedModes = (pipelines) => {
    const versionedModes = pipelines.reduce(
        (tempVersionedModes, { mode, version, default: isDefaultMode }) => {
            if (tempVersionedModes[mode]) {
                tempVersionedModes[mode].versions.push(version);
            } else {
                tempVersionedModes[mode] = {
                    versions: [version],
                    // A mode is the default one for a TGDT/system pair, it should not depend on the version
                    isDefault: isDefaultMode,
                };
            }

            return tempVersionedModes;
        },
        {}
    );

    // Sort versions in each mode for each trait group
    Object.values(versionedModes).forEach(({ versions }) => {
        versions.sort(rcompare);
    });

    return versionedModes;
};

TraitGroup.propTypes = {
    fieldArrayName: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    dataType: PropTypes.oneOf(Object.values(DATATYPES_ENUM)).isRequired,
    pipelines: PropTypes.arrayOf(
        PropTypes.shape(MODEL_PROPTYPES.Pipeline).isRequired
    ).isRequired,
    pipelineInstances:
        MODEL_PROPTYPES.ExperimentMissionTraitGroup.PipelineInstances
            .isRequired,
    validatedPipelineInstance:
        MODEL_PROPTYPES.ExperimentMissionTraitGroup.ValidatedPipelineInstance,
    uuid: PropTypes.string.isRequired,
    traitGroupDataTypeUuid: PropTypes.string.isRequired,
};
